import { CanvasElement, extractC4DeploymentModel } from './c4DeploymentModel';

export type C4AnsibleCluster = {
  id?: string;
  name?: string;
  display_name?: string;
  updated_at?: string;
  created_at?: string;
  last_scanned?: string;
  last_health_check?: string;
};

export type C4AnsibleFiles = {
  playbook: string;
  inventory: string;
};

const deploymentImageFor = (name: string): string => {
  if (name === 'discovery-service') return 'discovery-service:latest';
  if (name === 'meta-repository-service') return 'meta-registry-service:latest';
  return `${name}:latest`;
};

const timestampOf = (cluster: C4AnsibleCluster): number => {
  const raw = cluster.updated_at || cluster.last_scanned || cluster.last_health_check || cluster.created_at || '';
  const ts = Date.parse(raw);
  return Number.isFinite(ts) ? ts : 0;
};

export const selectLatestCluster = (clusters: C4AnsibleCluster[]): C4AnsibleCluster | null => {
  if (!clusters.length) return null;
  return clusters.slice().sort((a, b) => timestampOf(b) - timestampOf(a))[0];
};

export const generateAnsibleFromC4 = (
  elements: CanvasElement[],
  diagramName = 'Atlas',
  cluster: C4AnsibleCluster | null = null,
): C4AnsibleFiles => {
  const model = extractC4DeploymentModel(elements, diagramName);
  const namespace = model.namespace;
  const clusterId = cluster?.id || 'attached-cluster';
  const clusterName = cluster?.name || cluster?.display_name || clusterId;
  const yamlString = (value: string): string => `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
  const skippedComments = [
    ...model.unsupported.map((element) => `    # Skipped ${element.label}: ${element.unsupportedReason}`),
    ...model.databases
      .filter((database) => database.supportedEngine !== 'postgres')
      .map((database) => `    # Skipped ${database.label}: ${database.unsupportedReason}`),
  ].join('\n');

  const serviceTasks = model.services.map((service) => `
    - name: Apply ${service.label} Deployment
      kubernetes.core.k8s:
        state: present
        kubeconfig: "{{ kubeconfig_path }}"
        context: "{{ kube_context }}"
        definition:
          apiVersion: apps/v1
          kind: Deployment
          metadata:
            name: ${service.name}
            namespace: ${namespace}
            labels:
              app.kubernetes.io/name: ${service.name}
              app.kubernetes.io/part-of: ${namespace}
            annotations:
              architecture.aspect/display-name: ${yamlString(service.label)}
              architecture.aspect/c4-kind: ${yamlString(service.kind)}
              architecture.aspect/technology: ${yamlString(service.technology || 'unspecified')}
          spec:
            replicas: 1
            selector:
              matchLabels:
                app.kubernetes.io/name: ${service.name}
            template:
              metadata:
                labels:
                  app.kubernetes.io/name: ${service.name}
                  app.kubernetes.io/part-of: ${namespace}
              spec:
                containers:
                  - name: ${service.name}
                    image: ${deploymentImageFor(service.name)}
                    imagePullPolicy: IfNotPresent
                    ports:
                      - containerPort: 8080
                    readinessProbe:
                      httpGet:
                        path: /healthz
                        port: 8080
                      initialDelaySeconds: 10
                      periodSeconds: 10
                    livenessProbe:
                      httpGet:
                        path: /healthz
                        port: 8080
                      initialDelaySeconds: 30
                      periodSeconds: 20
                    resources:
                      requests:
                        cpu: 100m
                        memory: 128Mi
                      limits:
                        cpu: 500m
                        memory: 512Mi

    - name: Apply ${service.label} Service
      kubernetes.core.k8s:
        state: present
        kubeconfig: "{{ kubeconfig_path }}"
        context: "{{ kube_context }}"
        definition:
          apiVersion: v1
          kind: Service
          metadata:
            name: ${service.name}
            namespace: ${namespace}
            annotations:
              architecture.aspect/display-name: ${yamlString(service.label)}
          spec:
            selector:
              app.kubernetes.io/name: ${service.name}
            ports:
              - name: http
                port: 80
                targetPort: 8080
            type: ClusterIP`.trimEnd()).join('\n\n');

  const databaseTasks = model.databases
    .filter((database) => database.supportedEngine === 'postgres')
    .map((database) => `
    - name: Apply ${database.label} Secret
      kubernetes.core.k8s:
        state: present
        kubeconfig: "{{ kubeconfig_path }}"
        context: "{{ kube_context }}"
        definition:
          apiVersion: v1
          kind: Secret
          metadata:
            name: ${database.name}-credentials
            namespace: ${namespace}
            annotations:
              architecture.aspect/display-name: ${yamlString(database.label)}
          type: Opaque
          stringData:
            POSTGRES_DB: app
            POSTGRES_USER: app_user
            POSTGRES_PASSWORD: "{{ postgres_password }}"

    - name: Apply ${database.label} Service
      kubernetes.core.k8s:
        state: present
        kubeconfig: "{{ kubeconfig_path }}"
        context: "{{ kube_context }}"
        definition:
          apiVersion: v1
          kind: Service
          metadata:
            name: ${database.name}
            namespace: ${namespace}
            annotations:
              architecture.aspect/display-name: ${yamlString(database.label)}
          spec:
            selector:
              app.kubernetes.io/name: ${database.name}
            ports:
              - name: postgres
                port: 5432
                targetPort: 5432
            type: ClusterIP

    - name: Apply ${database.label} StatefulSet
      kubernetes.core.k8s:
        state: present
        kubeconfig: "{{ kubeconfig_path }}"
        context: "{{ kube_context }}"
        definition:
          apiVersion: apps/v1
          kind: StatefulSet
          metadata:
            name: ${database.name}
            namespace: ${namespace}
            labels:
              app.kubernetes.io/name: ${database.name}
              app.kubernetes.io/part-of: ${namespace}
            annotations:
              architecture.aspect/display-name: ${yamlString(database.label)}
          spec:
            serviceName: ${database.name}
            replicas: 1
            selector:
              matchLabels:
                app.kubernetes.io/name: ${database.name}
            template:
              metadata:
                labels:
                  app.kubernetes.io/name: ${database.name}
                  app.kubernetes.io/part-of: ${namespace}
              spec:
                containers:
                  - name: postgres
                    image: postgres:16-alpine
                    imagePullPolicy: IfNotPresent
                    envFrom:
                      - secretRef:
                          name: ${database.name}-credentials
                    ports:
                      - containerPort: 5432
                    resources:
                      requests:
                        cpu: 100m
                        memory: 256Mi
                      limits:
                        cpu: 500m
                        memory: 1Gi
                    volumeMounts:
                      - name: data
                        mountPath: /var/lib/postgresql/data
                        subPath: postgres
            volumeClaimTemplates:
              - metadata:
                  name: data
                spec:
                  accessModes: ["ReadWriteOnce"]
                  resources:
                    requests:
                      storage: 1Gi`.trimEnd()).join('\n\n');

  const playbook = `
---
- name: Deploy ${diagramName} to ${clusterName}
  hosts: local
  gather_facts: false
  vars:
    target_cluster_id: ${clusterId}
    target_cluster_name: ${clusterName}
    postgres_password: "{{ lookup('env', 'POSTGRES_PASSWORD') | default('', true) }}"
  collections:
    - kubernetes.core
  tasks:
    - name: Ensure ${namespace} namespace exists
      kubernetes.core.k8s:
        state: present
        kubeconfig: "{{ kubeconfig_path }}"
        context: "{{ kube_context }}"
        definition:
          apiVersion: v1
          kind: Namespace
          metadata:
            name: ${namespace}
            labels:
              app.kubernetes.io/part-of: ${namespace}

${skippedComments}

${serviceTasks}

${databaseTasks}
`.trimStart();

  const inventory = `
# Generated for C4 diagram "${diagramName}".
# Selected cluster: ${clusterName} (${clusterId}).
# Ensure kubeconfig_path points to a local kubeconfig with this context.
[local]
localhost ansible_connection=local

[local:vars]
target_cluster_id=${clusterId}
kube_context=${clusterName}
kubeconfig_path=./kubeconfig-${clusterName}.yaml
`.trimStart();

  return { playbook, inventory };
};
